import React, { useEffect, useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Printer, FileText, Receipt, ChevronDown, Mail, Link as LinkIcon, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { printTicketHTML } from "@/lib/ticketPrint";
import { printTicketViaQZ } from "@/lib/qzPrint";

// Mapea url del endpoint PDF al endpoint JSON del documento y al kind del ticket
function deriveDocMeta(pdfUrl) {
  // Ejemplos: /invoices/{id}/pdf, /quotations/{id}/pdf, /delivery-notes/{id}/pdf,
  //           /payments/{id}/pdf, /credit-debit-notes/{id}/pdf
  const m = pdfUrl.match(/^\/([\w-]+)\/([^/]+)\/pdf$/);
  if (!m) return null;
  const [, segment, id] = m;
  const kindMap = {
    "invoices": "invoice",
    "purchases": "purchase",
    "quotations": "quotation",
    "delivery-notes": "delivery_note",
    "payments": "payment",
    "credit-debit-notes": null, // requiere mirar doc.kind para decidir
  };
  return { jsonUrl: `/${segment}/${id}`, segment, kind: kindMap[segment] ?? null };
}

// Mapea el segmento de URL al settings key del print-format
const SEGMENT_TO_SETTING_KEY = {
  "invoices": "invoice",
  "purchases": "purchase",
  "expenses": "purchase",
  "quotations": "quotation",
  "delivery-notes": "delivery_note",
  "payments": "payment",
  "credit-debit-notes": "credit_note", // (también aplica a debit_note; usan mismo default)
};

function shouldUseTicket80(pdfUrl, cfg) {
  const meta = deriveDocMeta(pdfUrl);
  if (!meta) return false;
  const key = SEGMENT_TO_SETTING_KEY[meta.segment];
  if (!key) return false;
  return cfg[key] === "ticket80";
}

// Cache global del config print-format + qz para evitar pedir en cada PrintMenu
let _printCfgCache = null;
let _printCfgPromise = null;
let _qzCfgCache = null;
let _qzCfgPromise = null;
export async function getPrintConfig(force = false) {
  if (_printCfgCache && !force) return _printCfgCache;
  if (_printCfgPromise) return _printCfgPromise;
  _printCfgPromise = api.get("/settings/print-format")
    .then(({ data }) => { _printCfgCache = data; _printCfgPromise = null; return data; })
    .catch(() => { _printCfgPromise = null; return { direct_print: true }; });
  return _printCfgPromise;
}

export async function getQzConfig(force = false) {
  if (_qzCfgCache && !force) return _qzCfgCache;
  if (_qzCfgPromise) return _qzCfgPromise;
  _qzCfgPromise = api.get("/print/qz-config")
    .then(({ data }) => { _qzCfgCache = data; _qzCfgPromise = null; return data; })
    .catch(() => { _qzCfgPromise = null; return { enabled: false }; });
  return _qzCfgPromise;
}

export function invalidatePrintConfig() { _printCfgCache = null; _qzCfgCache = null; }

/**
 * Botón de impresión con menú desplegable (modo dual) o clic-directo (según ajustes).
 *
 * Props:
 *   url       — endpoint base (sin ?format=...). Ej: `/invoices/${id}/pdf`
 *   filename  — nombre sugerido del archivo (sin ext)
 *   label     — texto del botón principal (default "Imprimir")
 *   testid    — prefijo opcional de data-testid
 *   variant   — Button variant
 *   size      — Button size
 *   iconOnly  — solo icono
 */
export default function PrintMenu({
  url,
  filename = "documento",
  label = "Imprimir",
  testid = "print-menu",
  variant = "outline",
  size = "sm",
  iconOnly = false,
}) {
  const [cfg, setCfg] = useState(_printCfgCache || { direct_print: true });
  const [qzCfg, setQzCfg] = useState(_qzCfgCache || { enabled: false });
  const [emailOpen, setEmailOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    if (!_printCfgCache) {
      getPrintConfig().then((c) => setCfg(c));
    } else {
      setCfg(_printCfgCache);
    }
    if (!_qzCfgCache) {
      getQzConfig().then((c) => setQzCfg(c));
    } else {
      setQzCfg(_qzCfgCache);
    }
  }, []);

  // Compras/gastos: el ticket sale del PDF DGII (QR + código de seguridad); el HTML/ESC-POS no los cubre
  const pdfOnly = /^\/(purchases|expenses)\//.test(url);

  // Impresión vía QZ Tray (ESC/POS — cero papel desperdiciado)
  const printQz = async () => {
    if (pdfOnly) {
      await download("ticket80", true);
      return true;
    }
    const meta = deriveDocMeta(url);
    if (!meta) {
      toast.error("Tipo de documento no soportado por QZ");
      return false;
    }
    try {
      const { data: doc } = await api.get(meta.jsonUrl);
      let kind = meta.kind;
      if (!kind) {
        kind = doc.kind === "credit" ? "credit_note" : "debit_note";
      }
      const t = toast.loading("Enviando a la impresora térmica…");
      try {
        await printTicketViaQZ({ kind, docId: doc.id });
        toast.success("Ticket enviado a la impresora", { id: t });
        return true;
      } catch (err) {
        const msg = err?.message || String(err);
        if (msg.toLowerCase().includes("websocket")) {
          toast.error("No se pudo conectar con QZ Tray. ¿Está corriendo en tu PC?", { id: t });
        } else {
          toast.error(`Error de impresión: ${msg}`, { id: t });
        }
        return false;
      }
    } catch (err) {
      toast.error("No se pudo obtener el documento");
      return false;
    }
  };
  const printHtml = async (kindOverride = null) => {
    if (pdfOnly) return false;
    const meta = deriveDocMeta(url);
    if (!meta) return false;
    try {
      const [{ data: doc }, { data: company }] = await Promise.all([
        api.get(meta.jsonUrl),
        api.get("/settings/company"),
      ]);
      let kind = kindOverride || meta.kind;
      if (!kind) {
        kind = doc.kind === "credit" ? "credit_note" : "debit_note";
      }
      // Si es e-CF, traer QR + código de seguridad DGII (obligatorio Ley 32-23)
      let dgii = null;
      if (doc.is_electronic) {
        try {
          const printKind = kind === "credit_note" || kind === "debit_note"
            ? kind : (kind === "invoice" ? "invoice" : kind);
          const { data: pdata } = await api.get(
            `/print/escpos/${printKind}/${doc.id}`);
          dgii = pdata?.dgii || null;
        } catch (_) { /* best effort */ }
      }
      await printTicketHTML({ doc, company, kind, dgii });
      return true;
    } catch (err) {
      const st = err?.response?.status;
      toast.error(st === 404 ? "Documento no encontrado" : "No se pudo imprimir el ticket");
      return false;
    }
  };

  const download = async (format, autoprint = false) => {
    // Si autoprint + ticket80 → flujo HTML (sin papel desperdiciado)
    const effectiveFormat = format || cfg[Object.keys(cfg).find((k) => url.includes(`/${k.replace("_", "-")}s/`)) || ""];
    const useHtml = autoprint && (format === "ticket80" || (!format && shouldUseTicket80(url, cfg)));
    if (useHtml) {
      const ok = await printHtml();
      if (ok) return;
      // Si falla, cae al flujo PDF como respaldo
    }

    try {
      const qp = format ? `?format=${format}` : "";
      const { data } = await api.get(`${url}${qp}`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const u = URL.createObjectURL(blob);
      if (autoprint) {
        // Abrir en ventana nueva y disparar print automáticamente
        const w = window.open(u, "_blank");
        if (w) {
          const t = setTimeout(() => {
            try { w.focus(); w.print(); } catch (_) { /* noop */ }
          }, 700);
          w.addEventListener?.("beforeunload", () => clearTimeout(t));
        } else {
          toast.error("Tu navegador bloqueó la ventana de impresión. Permite popups.");
        }
        setTimeout(() => URL.revokeObjectURL(u), 60000);
      } else {
        const a = document.createElement("a");
        a.href = u;
        const suffix = format === "ticket80" ? "_ticket" : "";
        a.download = `${filename}${suffix}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 1000);
      }
    } catch (err) {
      const st = err?.response?.status;
      toast.error(st === 401 ? "Sesión expirada" : st === 404 ? "Documento no encontrado" : "No se pudo generar el PDF");
    }
  };

  // Derivar scope ("invoice" | "purchase" | "note") desde el URL del PDF.
  // Usado por el share flow (endpoints `/api/share/{scope}/{doc_id}/*`).
  const deriveScope = () => {
    const m = url.match(/^\/([\w-]+)\/([^/]+)\/pdf$/);
    if (!m) return null;
    const [, segment, id] = m;
    const scopeMap = {
      "invoices": "invoice",
      "purchases": "purchase",
      "credit-debit-notes": "note",
    };
    return scopeMap[segment] ? { scope: scopeMap[segment], id } : null;
  };

  const openEmailShare = async () => {
    const meta = deriveDocMeta(url);
    if (meta) {
      try {
        const { data: doc } = await api.get(meta.jsonUrl);
        setEmailTo(doc.customer_email || doc.supplier_email || "");
      } catch (_) { /* noop */ }
    }
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    const target = deriveScope();
    if (!target) { toast.error("Documento no soportado para enviar"); return; }
    if (!emailTo || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailTo)) {
      toast.error("Correo del destinatario inválido");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post(`/share/${target.scope}/${target.id}/email`, {
        to: emailTo, message: emailMsg || undefined,
      });
      toast.success(`Factura enviada a ${emailTo}`);
      setEmailOpen(false);
      setEmailMsg("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar";
      toast.error(msg);
    } finally {
      setEmailBusy(false);
    }
  };

  const openShareLink = async () => {
    const target = deriveScope();
    if (!target) { toast.error("Documento no soportado para compartir"); return; }
    setShareBusy(true);
    setShareUrl("");
    setShareCopied(false);
    setShareOpen(true);
    try {
      const { data } = await api.post(`/share/${target.scope}/${target.id}/link`);
      const abs = data.view_url?.startsWith("http")
        ? data.view_url : window.location.origin + data.view_url;
      setShareUrl(abs);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo crear el enlace";
      toast.error(msg);
      setShareOpen(false);
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success("Enlace copiado al portapapeles");
      setTimeout(() => setShareCopied(false), 2500);
    } catch (_) {
      toast.error("No se pudo copiar. Selecciona manualmente.");
    }
  };

  const shareDialogs = (
    <>
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid={`${testid}-email-dialog`}>
          <DialogHeader>
            <DialogTitle>Enviar comprobante por correo</DialogTitle>
            <DialogDescription>
              Adjunta el PDF del comprobante al destinatario. Resend envía desde tu dominio verificado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="share-to">Destinatario</Label>
              <Input id="share-to" type="email" value={emailTo}
                     placeholder="cliente@correo.com"
                     onChange={(e) => setEmailTo(e.target.value)}
                     data-testid={`${testid}-email-to`} />
            </div>
            <div>
              <Label htmlFor="share-msg">Mensaje (opcional)</Label>
              <Textarea id="share-msg" value={emailMsg}
                        placeholder="Hola, aquí tienes tu factura…"
                        onChange={(e) => setEmailMsg(e.target.value)}
                        data-testid={`${testid}-email-msg`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancelar</Button>
            <Button onClick={sendEmail} disabled={emailBusy} data-testid={`${testid}-email-send`}>
              {emailBusy ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid={`${testid}-share-dialog`}>
          <DialogHeader>
            <DialogTitle>Enlace para compartir</DialogTitle>
            <DialogDescription>
              Cualquier persona con este enlace podrá ver y descargar el PDF. Expira en 72 horas.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={shareBusy ? "Generando enlace..." : shareUrl}
                   data-testid={`${testid}-share-url`} />
            <Button variant="outline" size="icon" onClick={copyShareUrl}
                    disabled={!shareUrl} data-testid={`${testid}-share-copy`}>
              {shareCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          {shareUrl && (
            <div className="text-xs text-muted-foreground">
              Tip: abrir el enlace muestra la vista imprimible. El PDF directo está en{" "}
              <code className="text-[10px]">{shareUrl.replace("/view", "")}</code>.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const menuContent = (
    <DropdownMenuContent align="end" className="min-w-[260px]">
      {qzCfg.enabled && (
        <DropdownMenuItem onClick={() => printQz()} data-testid={`${testid}-print-qz`}>
          <Receipt className="w-4 h-4 mr-2 text-success" /> Imprimir en impresora térmica (QZ)
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => printHtml()} data-testid={`${testid}-print-ticket`}>
        <Receipt className="w-4 h-4 mr-2 text-primary" /> Imprimir Ticket 80mm (navegador)
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => download("letter", true)} data-testid={`${testid}-print-letter`}>
        <Printer className="w-4 h-4 mr-2" /> Imprimir PDF Carta
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => download("letter", false)} data-testid={`${testid}-letter`}>
        <FileText className="w-4 h-4 mr-2 opacity-60" /> Descargar PDF Carta
      </DropdownMenuItem>
      {deriveScope() && (<>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={openEmailShare} data-testid={`${testid}-email`}>
          <Mail className="w-4 h-4 mr-2 text-amber-600" /> Enviar por correo
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openShareLink} data-testid={`${testid}-share-link`}>
          <LinkIcon className="w-4 h-4 mr-2 text-sky-600" /> Copiar enlace para compartir
        </DropdownMenuItem>
      </>)}
    </DropdownMenuContent>
  );

  // Modo directo: split button — clic principal usa QZ Tray si está habilitado,
  // si no, imprime PDF/HTML directo. Chevron abre opciones avanzadas.
  if (cfg.direct_print) {
    const primaryAction = qzCfg.enabled
      ? () => printQz()
      : () => download(undefined, true);
    return (
      <>
      <div className="inline-flex items-center" data-testid={`${testid}-wrap`}>
        <Button
          variant={variant}
          size={size}
          className="gap-1.5 rounded-r-none"
          data-testid={`${testid}-trigger`}
          title={qzCfg.enabled ? "Imprimir en impresora térmica (QZ)" : "Imprimir (1 clic)"}
          onClick={primaryAction}
        >
          <Printer className="w-4 h-4" />
          {!iconOnly && <span>{label}</span>}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variant}
              size={size}
              className="rounded-l-none border-l-0 px-1.5"
              data-testid={`${testid}-more`}
              title="Más opciones de impresión"
              aria-label="Más opciones de impresión"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          {menuContent}
        </DropdownMenu>
      </div>
      {shareDialogs}
      </>
    );
  }

  // Modo NO directo: solo dropdown
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className="gap-1.5"
          data-testid={`${testid}-trigger`}
          title="Imprimir / Descargar"
        >
          <Printer className="w-4 h-4" />
          {!iconOnly && <span>{label}</span>}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      {menuContent}
    </DropdownMenu>
    {shareDialogs}
    </>
  );
}
